import { ENetworkRequestStatus } from 'services/BackendApi';
import * as Actions from './actions';
import { initialRecurringTaskListState, initialState, ITaskManagementDomain } from './model';
import { IRecurringTaskListFilters } from 'services/TaskManagementApi/queries/useRecurringTasks';
import { generateStableReminderUuid } from './helpers';
import { ITaskReminder } from 'services/TaskManagementApi/types/TaskManagementResponse';
import { uniqBy } from 'lodash-es';

type TUserListKey = 'assignees' | 'reporters' | 'reviewers';
type TUserRequestKey = 'getAssignees' | 'getReporters' | 'getReviewers';

const USER_LISTS: Record<Actions.TTaskUserListType, [TUserListKey, TUserRequestKey][]> = {
  assignee: [['assignees', 'getAssignees']],
  reporter: [['reporters', 'getReporters']],
  reviewer: [['reviewers', 'getReviewers']],
  all: [
    ['assignees', 'getAssignees'],
    ['reporters', 'getReporters'],
    ['reviewers', 'getReviewers'],
  ],
};

const setUserLists = (
  state: ITaskManagementDomain,
  typeOfUser: Actions.TTaskUserListType,
  users: ITaskManagementDomain['data'][TUserListKey],
  request: ENetworkRequestStatus
): ITaskManagementDomain => {
  const data = { ...state.data };
  const requests = { ...state.requests };
  USER_LISTS[typeOfUser].forEach(([listKey, requestKey]) => {
    data[listKey] = users;
    requests[requestKey] = request;
  });
  return { ...state, data, requests };
};

const setRecurringTaskFilter = (
  state: ITaskManagementDomain,
  changed: Partial<IRecurringTaskListFilters>
): ITaskManagementDomain => ({
  ...state,
  recurringTaskList: {
    ...state.recurringTaskList,
    filters: { ...state.recurringTaskList.filters, ...changed },
    pagination: { ...state.recurringTaskList.pagination, currentPage: 0 },
  },
});

export const taskManagementReducer = (
  state: ITaskManagementDomain = initialState,
  action: Actions.TaskManagementAction
): ITaskManagementDomain => {
  switch (action.type) {
    case Actions.SET_ID_FILTER:
      return {
        ...state,
        filters: {
          ...state.filters,
          id: action.value,
        },
      };

    case Actions.SET_CATEGORY_FILTER:
      return {
        ...state,
        filters: {
          ...state.filters,
          categories: action.value,
        },
      };

    case Actions.SET_STATUS_FILTER:
      return {
        ...state,
        filters: {
          ...state.filters,
          statuses: action.value,
        },
      };

    case Actions.SET_TEAM_FILTER:
      return {
        ...state,
        filters: {
          ...state.filters,
          team: action.value,
        },
      };

    case Actions.SET_ASSIGNEE_FILTER:
      return {
        ...state,
        filters: {
          ...state.filters,
          assignee: action.value,
        },
      };

    case Actions.SET_PRIORITY_FILTER:
      return {
        ...state,
        filters: {
          ...state.filters,
          priority: action.value,
        },
      };

    case Actions.SET_DATES_FILTER:
      return {
        ...state,
        filters: {
          ...state.filters,
          dates: action.value,
        },
      };

    case Actions.SET_BOOKING_REF_FILTER:
      return {
        ...state,
        filters: {
          ...state.filters,
          bookingRef: action.value,
        },
      };

    case Actions.SET_GUEST_NAME_FILTER:
      return {
        ...state,
        filters: {
          ...state.filters,
          guestName: action.value,
        },
      };

    case Actions.SET_REPORTER_FILTER:
      return {
        ...state,
        filters: {
          ...state.filters,
          reporter: action.value,
        },
      };

    case Actions.SET_REVIEWER_FILTER:
      return {
        ...state,
        filters: {
          ...state.filters,
          reviewer: action.value,
        },
      };

    case Actions.SET_RECURRING_FILTER:
      return {
        ...state,
        filters: {
          ...state.filters,
          recurring: action.value,
        },
      };

    case Actions.GET_FILTER_INFO_REQUEST:
      return {
        ...state,
        data: {
          ...state.data,
          categories: [],
          teams: [],
        },
        requests: {
          ...state.requests,
          getFilterInfo: ENetworkRequestStatus.PENDING,
        },
      };

    case Actions.GET_FILTER_INFO_SUCCESS:
      return {
        ...state,
        data: {
          ...state.data,
          categories: action.categories,
          teams: action.teams,
        },
        requests: {
          ...state.requests,
          getFilterInfo: ENetworkRequestStatus.SUCCESS,
        }
      };

    case Actions.GET_FILTER_INFO_FAILURE:
      return {
        ...state,
        data: {
          ...state.data,
          categories: [],
          teams: [],
        },
        requests: {
          ...state.requests,
          getFilterInfo: ENetworkRequestStatus.ERROR,
        },
      };

    case Actions.GET_USERS_REQUEST:
      return setUserLists(state, action.typeOfUser, [], ENetworkRequestStatus.PENDING);

    case Actions.GET_USERS_SUCCESS:
      return setUserLists(
        { ...state, data: { ...state.data, userCache: uniqBy([...action.users, ...state.data.userCache], 'uuid') } },
        action.typeOfUser,
        action.users,
        ENetworkRequestStatus.SUCCESS
      );

    case Actions.GET_USERS_FAILURE:
      return setUserLists(state, action.typeOfUser, [], ENetworkRequestStatus.ERROR);

    case Actions.GET_TASKS_REQUEST:
      return {
        ...state,
        data: {
          ...state.data,
          results: [],
          tableLoading: true,
        },
        requests: {
          ...state.requests,
          getTasks: ENetworkRequestStatus.PENDING,
        },
      };
    case Actions.GET_TASKS_SUCCESS:
      return {
        ...state,
        data: {
          ...state.data,
          results: action.tasks,
          tableLoading: false,
        },
        pagination: {
          ...state.pagination,
          totalItems: action.totalResults,
        },
        requests: {
          ...state.requests,
          getTasks: ENetworkRequestStatus.SUCCESS,
        },
      };
    case Actions.GET_TASKS_FAILURE:
      return {
        ...state,
        data: {
          ...state.data,
          results: [],
          tableLoading: false,
        },
        requests: {
          ...state.requests,
          getTasks: ENetworkRequestStatus.ERROR,
        },
      };
    case Actions.SET_PAGE_NUMBER:
      return {
        ...state,
        data: {
          ...state.data,
          results: [],
          tableLoading: true,
        },
        pagination: {
          ...state.pagination,
          currentPage: action.pageNumber,
        },
      };
    case Actions.SET_SORT:
      return {
        ...state,
        data: {
          ...state.data,
          tableLoading: true,
        },
        sort: {
          sortBy: action.sortBy,
          sortOrder: action.sortOrder,
        }
      };
    case Actions.SET_ITEMS_PER_PAGE:
      return {
        ...state,
        data: {
          ...state.data,
          tableLoading: true,
        },
        pagination: {
          ...state.pagination,
          currentPage: 0,
          itemsPerPage: action.itemsPerPage,
        }
      };
    case Actions.SET_RECURRING_TASK_SUBJECT_FILTER:
      return setRecurringTaskFilter(state, { subject: action.value });
    case Actions.SET_RECURRING_TASK_CATEGORY_FILTER:
      return setRecurringTaskFilter(state, { categoryIds: action.value });
    case Actions.SET_RECURRING_TASK_TEAM_FILTER:
      return setRecurringTaskFilter(state, { teamId: action.value });
    case Actions.SET_RECURRING_TASK_FREQUENCY_FILTER:
      return setRecurringTaskFilter(state, { frequencies: action.value });
    case Actions.SET_RECURRING_TASK_STATUS_FILTER:
      return setRecurringTaskFilter(state, { status: action.value });
    case Actions.RESET_RECURRING_TASK_FILTERS:
      return {
        ...state,
        recurringTaskList: {
          ...initialRecurringTaskListState,
          pagination: {
            ...initialRecurringTaskListState.pagination,
            itemsPerPage: state.recurringTaskList.pagination.itemsPerPage,
          },
        },
      };
    case Actions.SET_RECURRING_TASK_SORT:
      return {
        ...state,
        recurringTaskList: {
          ...state.recurringTaskList,
          sort: { sortBy: action.sortBy, sortOrder: action.sortOrder },
          pagination: { ...state.recurringTaskList.pagination, currentPage: 0 },
        },
      };
    case Actions.SET_RECURRING_TASK_PAGE_NUMBER:
      return {
        ...state,
        recurringTaskList: {
          ...state.recurringTaskList,
          pagination: { ...state.recurringTaskList.pagination, currentPage: action.pageNumber },
        },
      };
    case Actions.SET_RECURRING_TASK_ITEMS_PER_PAGE:
      return {
        ...state,
        recurringTaskList: {
          ...state.recurringTaskList,
          pagination: { currentPage: 0, itemsPerPage: action.itemsPerPage },
        },
      };
    case Actions.RESET_FILTERS:
      return {
        ...initialState,
      };
    case Actions.DELETE_TASK_REQUEST:
      return {
        ...state,
        requests: {
          ...state.requests,
          deleteTask: ENetworkRequestStatus.PENDING,
        }
      };
    case Actions.DELETE_TASK_SUCCESS:
      return {
        ...state,
        requests: {
          ...state.requests,
          deleteTask: ENetworkRequestStatus.SUCCESS,
        }
      };
    case Actions.DELETE_TASK_FAILURE:
      return {
        ...state,
        requests: {
          ...state.requests,
          deleteTask: ENetworkRequestStatus.ERROR,
        }
      };
    case Actions.CREATE_TASK_REQUEST:
      return {
        ...state,
        requests: {
          ...state.requests,
          createTask: ENetworkRequestStatus.PENDING,
        }
      };
    case Actions.CREATE_TASK_SUCCESS:
      return {
        ...state,
        requests: {
          ...state.requests,
          createTask: ENetworkRequestStatus.SUCCESS,
        }
      };
    case Actions.CREATE_TASK_FAILURE:
      return {
        ...state,
        requests: {
          ...state.requests,
          createTask: ENetworkRequestStatus.ERROR,
        }
      };

    case Actions.CREATE_RECURRING_TASK_REQUEST:
      return {
        ...state,
        requests: {
          ...state.requests,
          createRecurringTask: ENetworkRequestStatus.PENDING,
        },
        data: {
          ...state.data,
          recurrenceError: null,
        },
      };
    case Actions.CREATE_RECURRING_TASK_SUCCESS:
      return {
        ...state,
        requests: {
          ...state.requests,
          createRecurringTask: ENetworkRequestStatus.SUCCESS,
        },
      };
    case Actions.CREATE_RECURRING_TASK_FAILURE:
      return {
        ...state,
        requests: {
          ...state.requests,
          createRecurringTask: ENetworkRequestStatus.ERROR,
        },
        data: {
          ...state.data,
          recurrenceError: action.recurrenceError,
        },
      };

    case Actions.UPDATE_RECURRING_TASK_REQUEST:
      return {
        ...state,
        requests: {
          ...state.requests,
          updateRecurringTask: ENetworkRequestStatus.PENDING,
        },
        data: {
          ...state.data,
          recurrenceError: null,
        },
      };
    case Actions.UPDATE_RECURRING_TASK_SUCCESS:
      return {
        ...state,
        requests: {
          ...state.requests,
          updateRecurringTask: ENetworkRequestStatus.SUCCESS,
        },
      };
    case Actions.UPDATE_RECURRING_TASK_FAILURE:
      return {
        ...state,
        requests: {
          ...state.requests,
          updateRecurringTask: ENetworkRequestStatus.ERROR,
        },
        data: {
          ...state.data,
          recurrenceError: action.recurrenceError,
        },
      };

    case Actions.EDIT_TASK_REQUEST:
      return {
        ...state,
        requests: {
          ...state.requests,
          editTask: ENetworkRequestStatus.PENDING,
        }
      };
    case Actions.EDIT_TASK_SUCCESS:
      return {
        ...state,
        requests: {
          ...state.requests,
          editTask: ENetworkRequestStatus.SUCCESS,
        }
      };
    case Actions.EDIT_TASK_FAILURE:
      return {
        ...state,
        requests: {
          ...state.requests,
          editTask: ENetworkRequestStatus.ERROR,
        }
      };

    case Actions.GET_SELECTED_TASK_REQUEST:
      return {
        ...state,
        requests: {
          ...state.requests,
          getTask: ENetworkRequestStatus.PENDING,
        },
        data: {
          ...state.data,
          // clear the previous task's refusal so it doesn't stick to the one being opened
          selectedTaskAccessDenied: false,
        }
      };
    case Actions.GET_SELECTED_TASK_SUCCESS:
      return {
        ...state,
        requests: {
          ...state.requests,
          getTask: ENetworkRequestStatus.SUCCESS,
        },
        data: {
          ...state.data,
          selectedTask: action.task,
          selectedTaskAccessDenied: false,
          bookingRefs: action.task.booking ? [action.task.booking.humanReadableId] : [],
        }
      };
    case Actions.GET_SELECTED_TASK_FAILURE:
      return {
        ...state,
        requests: {
          ...state.requests,
          getTask: ENetworkRequestStatus.ERROR,
        },
        data: {
          ...state.data,
          selectedTask: action.isAccessDenied ? null : state.data.selectedTask,
          selectedTaskAccessDenied: action.isAccessDenied,
        }
      };
    case Actions.CREATE_TASK_COMMENT_SUCCESS:
      return {
        ...state,
        requests: {
          ...state.requests,
          createTaskComment: ENetworkRequestStatus.SUCCESS,
        }
      };
    case Actions.CREATE_TASK_COMMENT_REQUEST:
      return {
        ...state,
        requests: {
          ...state.requests,
          createTaskComment: ENetworkRequestStatus.PENDING,
        }
      };
    case Actions.ADD_FOCUSED_COMMENT:
      return {
        ...state,
        data: {
          ...state.data,
          focusedComments: [...state.data.focusedComments, action.commentUuid],
        }
      };
    case Actions.REMOVE_FOCUSED_COMMENT:
      return {
        ...state,
        data: {
          ...state.data,
          focusedComments: state.data.focusedComments.filter(item => item !== action.commentUuid),
        }
      };
    case Actions.CREATE_TASK_COMMENT_FAILURE:
      return {
        ...state,
        requests: {
          ...state.requests,
          createTaskComment: ENetworkRequestStatus.ERROR,
        }
      };
    case Actions.LIST_TASK_COMMENTS_REQUEST:
      return {
        ...state,
        requests: {
          ...state.requests,
          listComments: ENetworkRequestStatus.PENDING,
        }
      };
    case Actions.LIST_TASK_COMMENTS_SUCCESS:
      return {
        ...state,
        data: {
          ...state.data,
          comments: action.taskComments,
        },
        requests: {
          ...state.requests,
          listComments: ENetworkRequestStatus.SUCCESS,
        }
      };
    case Actions.LIST_TASK_COMMENTS_FAILURE:
      return {
        ...state,
        requests: {
          ...state.requests,
          listComments: ENetworkRequestStatus.ERROR,
        }
      };
    case Actions.DELETE_COMMENT_REQUEST:
      return {
        ...state,
        requests: {
          ...state.requests,
          deleteComment: ENetworkRequestStatus.PENDING,
        }
      };
    case Actions.DELETE_COMMENT_SUCCESS:
      return {
        ...state,
        requests: {
          ...state.requests,
          deleteComment: ENetworkRequestStatus.SUCCESS,
        }
      };
    case Actions.DELETE_COMMENT_FAILURE:
      return {
        ...state,
        requests: {
          ...state.requests,
          deleteComment: ENetworkRequestStatus.ERROR,
        }
      };
    case Actions.SET_SELECTED_COMMENT_ID:
      return {
        ...state,
        data: {
          ...state.data,
          selectedCommentId: action.commentId
        }
      };
      case Actions.ADD_TASK_REMINDER:
        return {
          ...state,
          data: {
            ...state.data,
            reminders: [ ...state.data.reminders, action.taskReminder ],
          },
        };
      case Actions.CHANGE_TASK_REMINDER:
        return {
          ...state,
          data: {
            ...state.data,
            reminders: state.data.reminders.map(item => {
              if (item.id === action.uuid) {
                return {
                  ...item,
                  channel: action.channel ? action.channel : item.channel,
                  offset: action.offset ?? item.offset,
                  offsetUnit: action.offsetUnit ? action.offsetUnit : item.offsetUnit,
                }
              } else {
                return item;
              }
            }),
          },
        };
      case Actions.DELETE_TASK_REMINDER:
        return {
          ...state,
          data: {
            ...state.data,
            reminders: state.data.reminders.filter(item => item.id !== action.uuid),
          },
        };
      case Actions.CLEAR_TASK_REMINDERS:
        return {
          ...state,
          data: {
            ...state.data,
            reminders: [],
          },
        };
      case Actions.LIST_TASK_REMINDERS_SUCCESS:
        return {
          ...state,
          data: {
            ...state.data,
            reminders: action.taskReminders,
          },
        };
      case Actions.OPEN_UPLOAD_TASK_ATTACHMENT_MODAL:
        return {
          ...state,
          data: {
            ...state.data,
            attachments: {
              ...state.data.attachments,
              uploadAttachmentModalOpen: true,
              newAttachment: {
                payload: {
                  file: null,
                  displayName: null,
                  tag: null,
                },
                request: ENetworkRequestStatus.IDLE,
                error: null,
              },
            }
          }
        };
  
      case Actions.CLOSE_UPLOAD_TASK_ATTACHMENT_MODAL:
        return {
          ...state,
          data: {
            ...state.data,
            attachments: {
              ...state.data.attachments,
              uploadAttachmentModalOpen: false,
              newAttachment: {
                ...state.data.attachments.newAttachment,
                payload: null,
              },
            }
          }
        };

    case Actions.SET_ATTACHMENT_TO_UPLOAD:
      return {
        ...state,
        data: {
          ...state.data,
          attachments: {
            ...state.data.attachments,
            newAttachment: {
              ...state.data.attachments.newAttachment,
              payload: {
                ...state.data.attachments.newAttachment.payload,
                file: action.document,
                displayName: action.document.name,
                tag: state.data.attachments.newAttachment.payload?.tag ?? null,
              },
              request: ENetworkRequestStatus.IDLE,
              error: null,
            },
          }
        },
      };

    case Actions.SET_ATTACHMENT_DISPLAY_NAME:
      return {
        ...state,
        data: {
          ...state.data,
          attachments: {
            ...state.data.attachments,
            newAttachment: {
              ...state.data.attachments.newAttachment,
              payload: {
                ...state.data.attachments.newAttachment.payload,
                file: state.data.attachments.newAttachment.payload?.file ?? null,
                displayName: action.displayName,
                tag: state.data.attachments.newAttachment.payload?.tag ?? null,
              },
              request: ENetworkRequestStatus.IDLE,
              error: null,
            },
          }
        }
      };

    case Actions.UPLOAD_ATTACHMENT_REQUEST:
      return {
        ...state,
        data: {
          ...state.data,
          attachments: {
            ...state.data.attachments,
            newAttachment: {
              ...state.data.attachments.newAttachment,
              request: ENetworkRequestStatus.PENDING,
              error: null,
            },
          }
        },
      };

    case Actions.UPLOAD_ATTACHMENT_SUCCESS:
      return {
        ...state,
        data: {
          ...state.data,
          attachments: {
            ...state.data.attachments,
            newAttachment: {
              ...state.data.attachments.newAttachment,
              request: ENetworkRequestStatus.SUCCESS,
              error: null,
            },
            uploadAttachmentModalOpen: false,
          }
        },
      };

    case Actions.UPLOAD_ATTACHMENT_FAILURE:
      return {
        ...state,
        data: {
          ...state.data,
          attachments: {
            ...state.data.attachments,
            newAttachment: {
              ...state.data.attachments.newAttachment,
              request: ENetworkRequestStatus.ERROR,
              error: action.error,
            },
            uploadAttachmentModalOpen: false,
          }
        },
      };

    case Actions.GET_MENTION_USERS_REQUEST:
      return {
        ...state,
        data: {
          ...state.data,
          mentionList: [],
        },
        requests: {
          ...state.requests,
          getMentionList: ENetworkRequestStatus.PENDING,
        },
      };

    case Actions.GET_MENTION_USERS_SUCCESS:
      return {
        ...state,
        data: {
          ...state.data,
          mentionList: action.users,
        },
        requests: {
          ...state.requests,
          getMentionList: ENetworkRequestStatus.SUCCESS,
        }
      };

    case Actions.GET_MENTION_USERS_FAILURE:
      return {
        ...state,
        data: {
          ...state.data,
          mentionList: [],
        },
        requests: {
          ...state.requests,
          getMentionList: ENetworkRequestStatus.ERROR,
        },
      };

    case Actions.LIST_ATTACHMENTS_REQUEST:
      return {
        ...state,
        requests: {
          ...state.requests,
          listAttachments: ENetworkRequestStatus.PENDING,
        },
      };

    case Actions.LIST_ATTACHMENTS_SUCCESS:
      return {
        ...state,
        requests: {
          ...state.requests,
          listAttachments: ENetworkRequestStatus.SUCCESS,
        },
        data: {
          ...state.data,
          attachments: {
            ...state.data.attachments,
            attachments: action.attachments,
          }
        },
      };

    case Actions.LIST_ATTACHMENTS_FAILURE:
      return {
        ...state,
        requests: {
          ...state.requests,
          listAttachments: ENetworkRequestStatus.ERROR,
        },
      };

    case Actions.DELETE_ATTACHMENT_REQUEST:
      return {
        ...state,
        requests: {
          ...state.requests,
          deleteAttachment: ENetworkRequestStatus.PENDING,
        }
      };

    case Actions.DELETE_ATTACHMENT_SUCCESS:
      return {
        ...state,
        requests: {
          ...state.requests,
          deleteAttachment: ENetworkRequestStatus.SUCCESS,
        },
        data: {
          ...state.data,
          selectedAttachmentId: null,
        }
      };
    case Actions.DELETE_ATTACHMENT_FAILURE:
      return {
        ...state,
        requests: {
          ...state.requests,
          deleteAttachment: ENetworkRequestStatus.ERROR,
        }
      };
    case Actions.VALIDATE_TASK_REMINDERS:
      // eslint-disable-next-line no-case-declarations
      const duplicatedReminderIds: string[] = [];
      // eslint-disable-next-line no-case-declarations
      const iteratedReminderIds: string[] = [];
      state.data.reminders.forEach((reminder: ITaskReminder) => {
        const reminderId = generateStableReminderUuid(reminder);
        if (iteratedReminderIds.includes(reminderId)) {
          duplicatedReminderIds.push(reminderId);
        }
        iteratedReminderIds.push(reminderId);
      });
      return {
        ...state,
        data: {
          ...state.data,
          duplicatedReminderIds,
        }
      };
    case Actions.CLEAN_DUPLICATED_TASK_REMINDER_IDS:
      return {
        ...state,
        data: {
          ...state.data,
          duplicatedReminderIds: [],
        }
      };
    case Actions.SET_SELECTED_ATTACHMENT_ID:
      return {
        ...state,
        data: {
          ...state.data,
          selectedAttachmentId: action.attachmentId
        }
      };
    case Actions.SEARCH_BOOKING_REF_REQUEST:
      return {
        ...state,
        data: {
          ...state.data,
          bookingRefs: [],
        },
        requests: {
          ...state.requests,
          searchBookingRefs: ENetworkRequestStatus.PENDING,
        },
      };
    case Actions.SEARCH_BOOKING_REF_SUCCESS:
      return {
        ...state,
        data: {
          ...state.data,
          bookingRefs: action.bookingRefs,
        },
        requests: {
          ...state.requests,
          searchBookingRefs: ENetworkRequestStatus.SUCCESS,
        },
      };
    case Actions.SEARCH_BOOKING_REF_FAILURE:
      return {
        ...state,
        requests: {
          ...state.requests,
          searchBookingRefs: ENetworkRequestStatus.ERROR,
        },
      };
    default:
      return state;
  }
};
